/**
 * Live conversation screen — a thin view over {@link LiveEngine}.
 *
 * All the session logic (WebSocket, mic capture + VAD segmentation, half-duplex
 * TTS playback, retry/backoff, demo mode) lives in the engine now; this file
 * only renders the engine's snapshot and forwards user intents (toggle, reset,
 * change guest language). UI mirrors the Human Connect design: clay/sage
 * two-speaker bubbles, mic dock with breathing halo, language pair header.
 */
import { memo, useEffect, useRef, useState } from "react";
import { authStore } from "../store";
import {
  Btn, IconBtn, LangBadge, SpeakerDot, Waveform, LevelMeter, Dots, Modal, LangList,
  langByCode, useLanguages,
} from "../ui";
import { Icon } from "../icons";
import type { Go } from "../App";
import { useLiveSession } from "../live/useLiveSession";
import type { Msg, LiveDemoMode } from "../live/engine";

export type { LiveDemoMode } from "../live/engine";

export function LiveScreen({ go, hasActive, demoMode, onFinalTurn }: { go: Go; hasActive: boolean; demoMode?: LiveDemoMode; onFinalTurn?: () => void }) {
  const user = authStore((s) => s.user);
  const langs = useLanguages();
  const youCode = user?.owner_lang ?? "en-US";
  const guestCode = user?.other_lang_override ?? user?.fallback_other_lang ?? "hi-IN";

  const { state, engine } = useLiveSession({ hasActive, youCode, guestCode, demoMode, onFinalTurn });
  const { msgs, capture, hearing, level, speakingId, errBanner } = state;

  const youLang = langByCode(state.youCode, langs);
  const guestLang = langByCode(state.guestCode, langs);

  const [pick, setPick] = useState(false);
  const [query, setQuery] = useState("");
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Autoscroll
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight + 800;
  }, [msgs, speakingId]);

  // Keep screen awake during active session (mobile)
  useEffect(() => {
    if (capture === "idle") return;
    let lock: WakeLockSentinel | null = null;
    let released = false;
    const acquire = () => {
      if (released) return;
      navigator.wakeLock?.request("screen")
        .then((l) => { if (!released) lock = l; else l.release(); })
        .catch(() => {});
    };
    acquire();
    // Re-acquire on visibility change (lock is auto-released when tab is hidden)
    const onVisChange = () => { if (document.visibilityState === "visible") acquire(); };
    document.addEventListener("visibilitychange", onVisChange);
    return () => {
      released = true;
      document.removeEventListener("visibilitychange", onVisChange);
      lock?.release().catch(() => {});
    };
  }, [capture !== "idle"]); // eslint-disable-line react-hooks/exhaustive-deps

  function onPickGuest(code: string) {
    setPick(false);
    setQuery("");
    engine.setGuest(code);
  }

  if (!hasActive) {
    return (
      <div style={{ height: "100%", display: "grid", placeItems: "center", padding: 30 }}>
        <div style={{ textAlign: "center", maxWidth: 380 }}>
          <div className="display" style={{ fontSize: 22, fontWeight: 700, color: "var(--ink)", marginBottom: 10 }}>
            Enroll your voice first
          </div>
          <p style={{ color: "var(--ink-3)", fontSize: 14.5, lineHeight: 1.5, marginBottom: 18 }}>
            We need a short voiceprint so the server can tell when you're speaking.
          </p>
          <Btn onClick={() => go("enroll")} icon="mic">Record my voice</Btn>
        </div>
      </div>
    );
  }

  const conn: "live" | "connecting" | "off" =
    capture === "listening" ? "live" : capture === "connecting" ? "connecting" : "off";
  const connColor =
    conn === "live" ? "var(--good)" : conn === "connecting" ? "var(--warn)" : "var(--ink-faint)";
  const running = capture === "listening" || capture === "connecting";
  const statusText =
    capture === "connecting" ? "Connecting…"
    : hearing ? "I can hear you"
    : capture === "listening" ? "Listening…"
    : capture === "trouble" ? "Tap to retry"
    : "Tap to resume";

  return (
    // Fill the parent's height instead of grabbing 100dvh directly. The parent
    // (App.tsx main or DemoScreen live wrapper) is what pins itself to the
    // dynamic viewport — doing it here too would push siblings (like the
    // demo banner) below the fold and force the document to scroll.
    <div style={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0, padding: "22px 28px 22px" }}>
      {/* header */}
      <div style={{ flexShrink: 0, paddingBottom: 16, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div style={{
          display: "flex", alignItems: "center", gap: 10, padding: "9px 16px",
          background: "var(--surface)", border: "1px solid var(--border)",
          borderRadius: "var(--r-pill)", boxShadow: "var(--shadow-sm)",
        }}>
          <SpeakerDot who="you" size={9} />
          <span style={{ fontSize: 14.5, fontWeight: 600, color: "var(--ink)" }}>You</span>
          <LangBadge short={youLang.short} who="you" size="sm" />
          <Icon name="arrowSwap" size={15} color="var(--ink-faint)" style={{ margin: "0 4px" }} />
          <button onClick={() => setPick(true)} style={{
            display: "flex", alignItems: "center", gap: 9, border: "none",
            background: "transparent", cursor: "pointer", padding: "4px 6px", borderRadius: 10,
          }}>
            <LangBadge short={guestLang.short} who="guest" size="sm" />
            <span style={{ fontSize: 14.5, fontWeight: 600, color: "var(--ink)" }}>{guestLang.name}</span>
            <Icon name="chevronDown" size={14} color="var(--ink-3)" />
          </button>
        </div>
        <div style={{
          display: "flex", alignItems: "center", gap: 8, padding: "7px 13px",
          borderRadius: "var(--r-pill)", background: "var(--surface-2)",
        }}>
          <span style={{
            width: 9, height: 9, borderRadius: "50%", background: connColor,
            animation: conn === "live" ? "hc-breathe 2.4s ease-in-out infinite" : "none",
          }} />
          <span className="mono" style={{ fontSize: 11.5, color: "var(--ink-2)", textTransform: "capitalize", letterSpacing: 0.3 }}>
            {conn === "off" ? "Paused" : conn}
          </span>
        </div>
        <div style={{ flex: 1 }} />
        <IconBtn name="refresh" onClick={() => engine.reset()} variant="surface" size={42} ic={18} title="Restart" />
      </div>

      {errBanner && (
        <div style={{
          flexShrink: 0, background: "color-mix(in oklch, var(--bad) 12%, transparent)",
          color: "var(--bad)", padding: "10px 14px", borderRadius: 12, fontSize: 13.5, marginBottom: 10,
          display: "flex", alignItems: "center", gap: 8,
        }}>
          <Icon name="shield" size={16} /> {errBanner}
          <button onClick={() => engine.clearError()} style={{
            marginLeft: "auto", background: "transparent", border: "none", cursor: "pointer", color: "var(--bad)",
            display: "flex", padding: 2,
          }}><Icon name="x" size={16} /></button>
        </div>
      )}

      {/* stream */}
      <div ref={scrollRef} className="hc-scroll" style={{
        flex: 1, minHeight: 0, overflowY: "auto", display: "flex", flexDirection: "column",
        gap: 22, padding: "4px 4px 8px",
      }}>
        {msgs.length === 0 ? (
          <Empty />
        ) : (
          msgs.map((m) => (
            <LiveBubble key={m.id} msg={m} speaking={speakingId === m.id} />
          ))
        )}
      </div>

      {/* mic dock */}
      <div style={{ flexShrink: 0, paddingTop: 16, display: "flex", justifyContent: "center" }}>
        <div style={{
          display: "flex", alignItems: "center", gap: 14, padding: "10px 22px 10px 10px",
          background: "var(--surface)", border: "1px solid var(--border)",
          borderRadius: "var(--r-pill)", boxShadow: "var(--shadow-md)", minWidth: "min(440px, 100%)",
        }}>
          <button onClick={() => engine.toggle()} style={{
            position: "relative", width: 56, height: 56, borderRadius: "50%",
            border: "none", cursor: "pointer", background: "transparent", flexShrink: 0,
          }}>
            {capture === "listening" && (
              <span style={{
                position: "absolute", inset: -4, borderRadius: "50%",
                background: "var(--accent)", opacity: hearing ? 0.4 : 0.18,
                animation: "hc-breathe 1.5s ease-in-out infinite",
              }} />
            )}
            <span style={{
              position: "absolute", inset: 0, borderRadius: "50%",
              background: "var(--accent)", display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: "var(--shadow-sm)",
            }}>
              <Icon name={running ? "pause" : "mic"} size={24} color="var(--on-accent)" />
            </span>
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 7 }}>
              {hearing && <Waveform active color="var(--accent)" bars={4} h={13} w={2.5} gap={2.5} />}
              {capture === "connecting" && (
                <span style={{
                  width: 13, height: 13, borderRadius: "50%",
                  border: "2px solid var(--border-strong)", borderTopColor: "var(--accent)",
                  animation: "hc-spin 0.8s linear infinite",
                }} />
              )}
              <span style={{ fontSize: 14.5, fontWeight: 600, color: "var(--ink-2)", whiteSpace: "nowrap" }}>
                {statusText}
              </span>
            </div>
            <LevelMeter level={hearing ? Math.max(0.1, level) : 0.04} />
          </div>
        </div>
      </div>

      <Modal open={pick} onClose={() => { setPick(false); setQuery(""); }} title="Guest is speaking…" sub="Pick the language to translate from.">
        <LangList activeCode={state.guestCode} who="guest" query={query} setQuery={setQuery} showRecents
          onPick={onPickGuest} />
      </Modal>
    </div>
  );
}

function Empty() {
  return (
    <div style={{
      flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
      justifyContent: "center", textAlign: "center", gap: 18,
    }}>
      <div style={{
        width: 92, height: 92, borderRadius: "50%", background: "var(--surface-2)",
        display: "grid", placeItems: "center", border: "1px solid var(--border)",
      }}>
        <Icon name="messages" size={36} color="var(--ink-faint)" stroke={1.7} />
      </div>
      <div>
        <div className="display" style={{ fontSize: 24, fontWeight: 600, color: "var(--ink)", marginBottom: 8 }}>
          Put the device between you
        </div>
        <div style={{ fontSize: 15, color: "var(--ink-3)", lineHeight: 1.55, maxWidth: 380 }}>
          One mic listens to both of you. Start talking — Aevum recognises who's
          speaking and handles the turns.
        </div>
      </div>
    </div>
  );
}

// Memoized so the ~31/sec level-meter updates (which re-render the LiveScreen
// root) don't re-render every bubble in the message list.
const LiveBubble = memo(function LiveBubble({ msg, speaking }: { msg: Msg; speaking: boolean }) {
  const you = msg.who === "you";
  const accent = you ? "var(--you)" : "var(--guest)";
  const langs = useLanguages();
  const srcShort = langByCode(msg.srcLang, langs).short;
  const dstShort = langByCode(msg.dstLang, langs).short;
  const cssVar = { "--ring-c": accent } as unknown as React.CSSProperties;
  return (
    <div style={{ display: "flex", justifyContent: you ? "flex-end" : "flex-start", animation: "hc-pop 0.34s var(--ease)" }}>
      <div style={{ maxWidth: "min(620px, 80%)", minWidth: 240 }}>
        <div style={{
          display: "flex", alignItems: "center", gap: 8, marginBottom: 6,
          justifyContent: you ? "flex-end" : "flex-start", padding: "0 6px",
        }}>
          {!you && <SpeakerDot who="guest" size={9} />}
          <span style={{ fontSize: 13, fontWeight: 700, color: you ? "var(--you-meta)" : "var(--guest-meta)" }}>
            {you ? "You" : "Guest"}
          </span>
          <LangBadge short={srcShort} who={msg.who} size="sm" />
          {you && <SpeakerDot who="you" size={9} />}
        </div>
        <div style={{
          background: you ? "var(--you-bubble)" : "var(--guest-bubble)",
          borderRadius: you ? "22px 22px 8px 22px" : "22px 22px 22px 8px",
          padding: "16px 19px 15px", position: "relative",
          boxShadow: speaking ? undefined : "var(--shadow-sm)",
          animation: speaking ? "hc-speakring 1.6s ease-in-out infinite" : "none",
          outline: speaking ? `1.5px solid color-mix(in oklch, ${accent} 60%, transparent)` : "1.5px solid transparent",
          transition: "outline-color 0.3s",
          ...cssVar,
        }}>
          <div style={{
            fontSize: 21, lineHeight: 1.34, fontWeight: 500,
            color: you ? "var(--you-ink)" : "var(--guest-ink)",
          }}>
            {msg.src || (msg.phase === "partial" ? "…" : "")}
            {msg.phase === "partial" && (
              <span style={{
                display: "inline-block", width: 2, height: 18, background: accent,
                marginLeft: 3, verticalAlign: -3, animation: "hc-breathe 1s steps(1) infinite",
              }} />
            )}
          </div>
          {msg.srcLang !== msg.dstLang && (
            <div style={{
              marginTop: 12, paddingTop: 12,
              borderTop: `1px solid color-mix(in oklch, ${accent} 22%, transparent)`,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: msg.dst ? 6 : 0 }}>
                <Icon name="languages" size={13} color={you ? "var(--you-meta)" : "var(--guest-meta)"} stroke={2} />
                <span className="mono" style={{
                  fontSize: 11, color: you ? "var(--you-meta)" : "var(--guest-meta)", letterSpacing: 0.4,
                }}>→ {dstShort}</span>
                {!msg.dst && !msg.unspoken && (
                  <span style={{ marginLeft: 4, display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <Dots color={you ? "var(--you-meta)" : "var(--guest-meta)"} />
                    <span style={{ fontSize: 12, color: you ? "var(--you-meta)" : "var(--guest-meta)" }}>translating</span>
                  </span>
                )}
                {msg.unspoken && (
                  <span style={{
                    marginLeft: 4, display: "inline-flex", alignItems: "center", gap: 5,
                    color: you ? "var(--you-meta)" : "var(--guest-meta)", opacity: 0.8,
                  }}>
                    <Icon name="volumeX" size={13} stroke={2} />
                    <span style={{ fontSize: 11.5 }}>not spoken</span>
                  </span>
                )}
                {speaking && (
                  <span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 6, color: accent }}>
                    <Waveform active color={accent} bars={5} h={14} w={2.5} gap={2.5} />
                    <span style={{ fontSize: 11.5, fontWeight: 700 }}>Speaking</span>
                  </span>
                )}
              </div>
              {msg.dst && (
                <div style={{
                  fontSize: 18, lineHeight: 1.36, fontWeight: 500,
                  color: you ? "var(--you-ink)" : "var(--guest-ink)", opacity: 0.92,
                }}>{msg.dst}</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});
